import {
  CampaignUrlFieldName,
  GsmCodePoints,
  RegExExtractUrlsWithNoAnchorTagsFromSmsText,
  ValidColumnNameRegExpStr
} from '../constants';
import { smsTextWithShortenLinks } from './shortenLinksInSmsText';
import _ from 'lodash';
import { SmsHtmlParser } from './smsHtmlParser';

export const GsmSmsMessageMaxLength = 160;
export const GsmSmsMessageMaxConcatenatedLength = 153;
export const GsmSmsMaxLength = GsmSmsMessageMaxConcatenatedLength * 10;
export const UcsSmsMessageMaxLength = 70;
export const UcsSmsMessageMaxConcatenatedLength = 67;
export const UcsSmsMaxLength = UcsSmsMessageMaxConcatenatedLength * 10 - 1;
export const MaxSmsMessageCount = 10;
export const MmsMaxLength = 1530;
export const MmsCreditCost = 3;

export class Sms {
  private smsText: string;
  private isMms: boolean;
  private hasVcard: boolean;
  constructor(smsText: string, isMms = false, hasVcard = false) {
    this.smsText = smsText;
    this.isMms = isMms;
    this.hasVcard = hasVcard;
  }
  // Twilio sms character calculator: https://twiliodeved.github.io/message-segment-calculator/
  // How Twilio handles unicode: https://www.twilio.com/blog/2015/08/common-sms-problems-unicode-twilio.html

  // In summary:
  // Unicode supports Bmp and non Bmp characters (Basic Multilingual Plane).
  // Bmp characters are the first 65535 chars. They require 16 bits of data. Non Bmp are larger and require 32 bits.
  // emojis are examples of non Bmp characters.
  // unicode supports non Bmp by using surrogate pairs ie 2 16bit characters

  // Twilio sms sending uses Ucs2 encoding for characters outside of GSM-7. Ucs2 ONLY supports Bmp characters.
  // 2 characters or 32 bits are used to support non Bmp characters
  // Hence for the purposes of sms sending, non Bmp characters like emojis should count as 2 characters and not 1

  // Javascript uses utf-16, so 32 bit chars like emoji, have a length of 2 in javascript.
  // Maximum maximum Bmp code point = 0xffff;
  private invalidGsmChars(s: string): string[] {
    const chars = s.replace(/\s/g, '').split('');
    const nonGsm7Chars = chars.filter(char => {
      const charCodePoint = char.codePointAt(0);
      const isGsm7 = charCodePoint && GsmCodePoints.includes(charCodePoint);
      return !isGsm7;
    });
    return nonGsm7Chars;
  }

  private getSmsTextLength(): number {
    return this.getSmsTextWithoutEscapedCharacters().length;
  }

  private getSmsTextWithoutEscapedCharacters(): string {
    //remove anchor tags
    let cleanText = this.smsText.replace(/<\/?a[^>]*>/g, '');
    //decodes escaped HTML, if there are any
    cleanText = this.decodeHtml(cleanText);
    return cleanText;
  }

  private decodeHtml(str: string): string {
    const textArea = document.createElement('textarea');
    textArea.innerHTML = str;
    return textArea.value;
  }

  getSingleMessageMaxLength(characterCount: number): number {
    if (this.isMms) return MmsMaxLength;

    const singleMessageMaxLength = this.isGsmSms()
      ? GsmSmsMessageMaxLength
      : UcsSmsMessageMaxLength;
    if (!characterCount || characterCount <= singleMessageMaxLength)
      return singleMessageMaxLength;
    // if concatenated sms
    return this.isGsmSms()
      ? GsmSmsMessageMaxConcatenatedLength
      : UcsSmsMessageMaxConcatenatedLength;
  }

  isValidGsmString(): boolean {
    return !this.invalidGsmChars(this.smsText).length;
  }

  isGsmSms(): boolean {
    return this.smsText ? this.isValidGsmString() : true;
  }

  getSmsMessageCount(characterCount: number, messageCountOffset = 0): number {
    const messageLength =
      (characterCount > 0 ? characterCount : this.getSmsTextLength()) +
      messageCountOffset;

    const messageMaxLength = this.getSingleMessageMaxLength(messageLength);
    return Math.ceil(messageLength / messageMaxLength);
  }

  getCreditsCount(characterCount: number, messageCountOffset = 0): number {
    if (this.isMms && this.hasVcard) {
      return MmsCreditCost * 2;
    }
    if (this.isMms || this.hasVcard) {
      return MmsCreditCost;
    }
    return this.getSmsMessageCount(characterCount, messageCountOffset);
  }

  getNumberOfMessages(characterCount: number): number {
    const messageMaxLength = this.getSingleMessageMaxLength(characterCount);
    return Math.ceil(characterCount / messageMaxLength);
  }

  getMaxCharacterCount(): number {
    if (this.isMms) return MmsMaxLength;
    return this.isGsmSms() ? GsmSmsMaxLength : UcsSmsMaxLength;
  }

  extractAndShortenLinksInSmsText(
    maxNumberOfLinksToBeShorten: number,
    shortenLinkPrefix: string
  ): {
    smsTextWithShortenLinks: string;
    originalSmsLinks: string[];
    numberOfLinksInSms: number;
  } {
    const originalSmsLinks: string[] = [];
    let linkCount = 0;
    const linksArray = ['xxxxx', 'yyyyy', 'zzzzz', 'aaaaa', 'bbbbb'];

    const smsTextWithShortenLinks = this.smsText.replace(
      RegExExtractUrlsWithNoAnchorTagsFromSmsText,
      (match: string) => {
        linkCount++;

        if (linkCount > maxNumberOfLinksToBeShorten) return match;

        originalSmsLinks.push(match.trim());

        return (
          (match.startsWith(' ') ? ' ' : '') +
          `${shortenLinkPrefix}/v/${linksArray[linkCount - 1]}` +
          (match.endsWith(' ') ? ' ' : '')
        );
      }
    );

    return {
      smsTextWithShortenLinks,
      originalSmsLinks,
      numberOfLinksInSms: linkCount
    };
  }

  hasLinksToShorten() {
    const matches =
      this.smsText.match(RegExExtractUrlsWithNoAnchorTagsFromSmsText) ?? [];
    return !!matches.length;
  }
}

export function getSmsWeightedLength(
  editorText: string,
  fields,
  includeCampaignUrl,
  campaignUrlCharacterWeight,
  personalizationFieldCharacterWeight,
  shortenMyLinks,
  shortenedLinkDomain
): number {
  const fieldIdToNameMapping = _.mapValues(fields, 'name');

  const editorTextSnapshot = editorText;
  const editorTextSnapshotWithoutNewLine = SmsHtmlParser.trimNewLineCharacters(
    editorTextSnapshot
  );

  const editorTextWithTagWeightApplied = editorTextSnapshotWithoutNewLine.replace(
    new RegExp(`{{\\s*(${ValidColumnNameRegExpStr})\\s*}}`, 'g'),
    (match, matchedFieldId) => {
      const isValid = isValidTag(
        fieldIdToNameMapping,
        matchedFieldId,
        includeCampaignUrl
      );

      if (isValid) {
        const isCampaignUrlTag = matchedFieldId === CampaignUrlFieldName;
        const tagWeight = isCampaignUrlTag
          ? campaignUrlCharacterWeight
          : personalizationFieldCharacterWeight;
        const TagWeightPlaceholderCharacter = ' ';
        return TagWeightPlaceholderCharacter.repeat(tagWeight);
      } else {
        return match;
      }
    }
  );

  const { originalSmsLinks } = smsTextWithShortenLinks(
    editorText,
    shortenedLinkDomain
  );

  return shortenMyLinks
    ? editorTextWithTagWeightApplied.length -
        originalLinksTotalLength(originalSmsLinks) +
        shortenLinkLength(originalSmsLinks, shortenedLinkDomain)
    : editorTextWithTagWeightApplied.length;
}

function removeNewLineCharAtEnd(text: string): string {
  const lastCharIsNewLine = text[text.length - 1] === '\n';
  return text.substring(0, lastCharIsNewLine ? text.length - 1 : text.length);
}

function isValidTag(fieldIdToNameMapping, matchedFieldId, includeCampaignUrl) {
  return (
    fieldIdToNameMapping[matchedFieldId] ||
    (includeCampaignUrl && matchedFieldId === CampaignUrlFieldName)
  );
}

export function shortenLinkLength(originalLinks, shortenedLinkDomain?) {
  if (!originalLinks.length) return 0;

  //Adding 5 for the number in the url e.g. https://dev2.mob.ly/v/xxxxx
  const domain = shortenedLinkDomain
    ? shortenedLinkDomain
    : process.env.ShortenLinkBaseUrl;

  const NumberOfCharactersInShortenLink = `${shortenedLinkDomain}/v/xxxxx`
    .length;

  return originalLinks.length * NumberOfCharactersInShortenLink;
}

function originalLinksTotalLength(originalLinks) {
  if (!originalLinks.length) return 0;

  return originalLinks.reduce(
    (runningTotal, currentValue) => runningTotal + currentValue.length,
    0
  );
}
